import { watch, watchSubscriptions } from "~/state/util";
import { Unsubscribe } from "redux";
import { IPosition, ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { store } from "~/state/store";
import { selectActiveSubscription, selectSubscriptions } from "~/state/share-position/selectors";
import AccuracyMarker from "~/ui/markers/accuracy";
import AvatarMarker from "~/ui/markers/avatar";
import { setHoverSubscription, toggleActiveSubscription } from "~/state/share-position/state";
import { addBadge } from "~/search/markers";
import { isDetailActive } from "~/util/detail";
import { filterSubscriptions, routeParts2Position } from "~/share-position2/utils";
import { CONFIG } from "~/config";
import Scene from "~/scene";
import { reviewHasColoredBadge } from "~/util/review";
import { description } from "~/util/providers/poiagg";
import { addListener, removeListener } from "~/lib/signals";
import { setCoordinates } from "~/state/map/state";

interface IMapSubscription {
	accuracy: any; // AccuracyMarker
	avatar: any; // AvatarMarker
	geometryGone: any; // SMap.Geometry
	geometryToGo: any; // SMap.Geometry
	start: any; // Mapy.MarkerSearch
	destination: any; // Mapy.MarkerSearch
}

const TOGO_LINE_STYLE = {
	width: 8,
	color: "#0033ff",
	opacity: 0.6,
	outlineWidth: 2,
	outlineColor: "white",
	outlineOpacity: 0.4,
	style: JAK.Vector.STYLE_SOLID,
	endCap: JAK.Vector.END_STYLE_ROUND,
};

const GONE_LINE_STYLE = {
	width: 8,
	color: "#454c54",
	opacity: 0.6,
	outlineWidth: 2,
	outlineColor: "white",
	outlineOpacity: 0.4,
	style: JAK.Vector.STYLE_SOLID,
	endCap: JAK.Vector.END_STYLE_ROUND,
};

/** zoom, na který se nastaví mapa při aktivaci sledování, které nemá geometrii a tedy se nedělá bestfit */
const NO_GEOMETRY_ZOOM = 20;

export default class SharePosition {
	#storeUnsubscribeSharePosition: Unsubscribe;

	#storeUnsubscribeSharePositionActive: Unsubscribe;

	#storeUnsubscribeSharePositionHover: Unsubscribe;

	#accuracyLayer: any; // SMap.Layer.Marker

	#avatarLayer: any; // SMap.Layer.Marker

	#markerLayer: any; // SMap.Layer.Marker

	#geometryLayer: any; // SMap.Layer.Geometry

	#subscriptions: {
		[key: string]: IMapSubscription;
	};

	#map: any; // SMap

	#sharepositionActivatedListener: string;

	#sharepositionDeactivatedListener: string;

	constructor(map: any /* SMap */) {
		this.#subscriptions = {};
		this.#map = map;

		// vrstva pro presnost
		this.#accuracyLayer = new SMap.Layer.Marker();
		map.addLayer(this.#accuracyLayer);
		this.#accuracyLayer.enable();
		// vrstva markeru pro sledovani polohy
		this.#avatarLayer = new SMap.Layer.Marker();
		map.addLayer(this.#avatarLayer);
		this.#avatarLayer.enable();
		// vrstva geometrii pro sledovani polohy
		this.#geometryLayer = new SMap.Layer.Geometry();
		map.addLayer(this.#geometryLayer);
		this.#geometryLayer.enable();
		// vrstva pro markery cilu
		this.#markerLayer = new SMap.Layer.Marker();
		map.addLayer(this.#markerLayer);
		this.#markerLayer.enable();

		// precteme napoprve ze store
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);
		const activeSubsciption = selectActiveSubscription(storeState);

		this.#sharePositionVisualize(subscriptions, activeSubsciption, activeSubsciption);

		// nahodime sledovani stavu ve store
		const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);
		const wtSharePositionActiveSubscription = watch<string>(store.getState, "sharePosition.activeSubscription");
		const wtSharePositionHoverSubscription = watch<string>(store.getState, "sharePosition.hoverSubscription");

		this.#storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(newSubscriptions => {
			const activeSub = selectActiveSubscription(store.getState());

			this.#sharePositionVisualize(newSubscriptions, activeSub, activeSub);
		}));
		this.#storeUnsubscribeSharePositionActive = store.subscribe(wtSharePositionActiveSubscription((activeSub, oldActiveSub) => {
			const subs = selectSubscriptions(store.getState());

			this.#sharePositionVisualize(subs, activeSub, oldActiveSub);
		}));
		this.#storeUnsubscribeSharePositionHover = store.subscribe(wtSharePositionHoverSubscription((hoverSub, oldHoverSub) => {
			if (oldHoverSub) {
				this.#subscriptions[oldHoverSub]?.avatar.setSelected(false);
				this.#subscriptions[oldHoverSub]?.avatar.removeCard();
			}

			if (hoverSub) {
				this.#subscriptions[hoverSub]?.avatar.setSelected(true);
				this.#subscriptions[hoverSub]?.avatar.showCard();
			}
		}));

		this.#sharepositionActivatedListener = addListener(this, "shareposition-activated", "sharepositionActivated");
		this.#sharepositionDeactivatedListener = addListener(this, "shareposition-deactivated", "sharepositionDeactivated");
	}

	destroy() {
		// zruseni sledovani pozice
		this.#storeUnsubscribeSharePosition();
		this.#storeUnsubscribeSharePositionActive();
		this.#storeUnsubscribeSharePositionHover();
		this.#clearSharePosition();

		if (this.#accuracyLayer) {
			this.#accuracyLayer.disable();
			this.#map.removeLayer(this.#accuracyLayer);
			this.#accuracyLayer.$destructor();
			this.#accuracyLayer = null;
		}

		if (this.#avatarLayer) {
			this.#avatarLayer.disable();
			this.#map.removeLayer(this.#avatarLayer);
			this.#avatarLayer.$destructor();
			this.#avatarLayer = null;
		}

		if (this.#geometryLayer) {
			this.#geometryLayer.disable();
			this.#map.removeLayer(this.#geometryLayer);
			this.#geometryLayer.$destructor();
			this.#geometryLayer = null;
		}

		if (this.#markerLayer) {
			this.#markerLayer.disable();
			this.#map.removeLayer(this.#markerLayer);
			this.#markerLayer.$destructor();
			this.#markerLayer = null;
		}

		removeListener(this.#sharepositionActivatedListener);
		removeListener(this.#sharepositionDeactivatedListener);
	}

	#sharePositionVisualize(subscriptions: Array<ISubscriptionWithDescription>, activeId: string, oldActiveId: string) {
		const filteredSubscriptions = filterSubscriptions(subscriptions);

		// pokud je nove aktivni, schovame geometrii toho stareho, pokud byla videt
		if (activeId !== oldActiveId) {
			if (oldActiveId in this.#subscriptions) {
				const { geometryGone, geometryToGo, start, destination } = this.#subscriptions[oldActiveId];

				try {
					this.#geometryLayer.removeGeometry(geometryGone);
					this.#geometryLayer.removeGeometry(geometryToGo);
					start && this.#markerLayer.removeMarker(start);
					destination && this.#markerLayer.removeMarker(destination);
				} catch (ex) {
					//
				}
			}
		}

		// aktualizujeme, co je videt a co uz zahazujeme
		if (filteredSubscriptions.length) {
			const newSubscriptions: { [key: string]: IMapSubscription; } = {};

			filteredSubscriptions.forEach((subscription, subscriptionIndex) => {
				// mame takovou subscription uz ted? tak jen updatujeme jeji data a poradi ve vypisu
				if (subscription.subscriptionId in this.#subscriptions) {
					const { accuracy, avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[subscription.subscriptionId];

					// geometrie
					const [coordsGone, coordsToGo] = routeParts2Position(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0).map(part => part.map(position => Scene.coordsFromWGS84(position[0], position[1])));

					// avatar
					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords.toWGS84()[0] : subscription.position?.lon || 0;
					const lat = useGeometryCoords ? geometryCoords.toWGS84()[1] : subscription.position?.lat || 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};
					const markerData = {
						...subscription,
						position,
					};

					accuracy.updateData(markerData);
					avatar.updateData(markerData);
					avatar.setSelected(activeId === subscription.subscriptionId);
					avatar.setIndex(subscriptionIndex);

					const avatars = this.#avatarLayer.getMarkers();
					let addAvatar = true;

					for (let index = 0; index < avatars.length; index++) {
						if (avatars[index] === avatar) {
							if (subscription.attributes.showOnMap || Mapy.getComponent("sharePosition").isActive()) {
								addAvatar = false;
							} else {
								this.#accuracyLayer.removeMarker(accuracy);
								this.#avatarLayer.removeMarker(avatar);
							}

							break;
						}
					}

					if ((addAvatar && subscription.attributes.showOnMap) || Mapy.getComponent("sharePosition").isActive()) {
						this.#accuracyLayer.addMarker(accuracy);
						this.#avatarLayer.addMarker(avatar);
					}

					accuracy.setSelected(activeId === subscription.subscriptionId);
					geometryGone.setCoords(coordsGone);
					geometryToGo.setCoords(coordsToGo);

					if (coordsGone.length || coordsToGo.length) {
						// start
						start?.setCoords((coordsGone.length ? coordsGone[0] : coordsToGo[0]).clone());

						// cil
						destination?.setCoords((coordsToGo.length ? coordsToGo[coordsToGo.length - 1] : coordsGone[coordsGone.length - 1]).clone());
					}

					// dame si item do noveho objektu a ze stareho odstranime
					newSubscriptions[subscription.subscriptionId] = this.#subscriptions[subscription.subscriptionId];
					delete this.#subscriptions[subscription.subscriptionId];
				// je to nova vec, vyrobime ji
				} else {
					// geometrie
					const [coordsGone, coordsToGo] = routeParts2Position(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0).map(part => part.map(position => Scene.coordsFromWGS84(position[0], position[1])));
					const geometryGone = new SMap.Geometry(SMap.GEOMETRY_POLYLINE, undefined, coordsGone, GONE_LINE_STYLE);
					const geometryToGo = new SMap.Geometry(SMap.GEOMETRY_POLYLINE, undefined, coordsToGo, TOGO_LINE_STYLE);

					// avatar
					const activate = (event: PointerEvent) => {
						event.stopPropagation();
						store.dispatch(toggleActiveSubscription(subscription.subscriptionId));
					};
					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords.toWGS84()[0] : subscription.position?.lon || 0;
					const lat = useGeometryCoords ? geometryCoords.toWGS84()[1] : subscription.position?.lat || 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};
					const markerData = {
						...subscription,
						position,
					};
					const accuracy = new AccuracyMarker(markerData);
					const avatar = new AvatarMarker(markerData, {
						onClick: activate,
						onCardClick: activate,
						index: subscriptionIndex,
					});
					const container = avatar.getContainer()[SMap.LAYER_MARKER];

					container.addEventListener("pointerenter", () => {
						store.dispatch(setHoverSubscription(subscription.subscriptionId));
					}, false);
					container.addEventListener("pointerleave", () => {
						store.dispatch(setHoverSubscription(""));
					}, false);
					avatar.setSelected(activeId === subscription.subscriptionId);
					if (subscription.attributes.showOnMap || Mapy.getComponent("sharePosition").isActive()) {
						this.#accuracyLayer.addMarker(accuracy);
						this.#avatarLayer.addMarker(avatar);
					}
					accuracy.setSelected(activeId === subscription.subscriptionId);

					// start
					let start = null;

					if (coordsGone.length || coordsToGo.length) {
						// @ts-ignore
						start = new Mapy.MarkerSearch((coordsGone.length ? coordsGone[0] : coordsToGo[0]).clone(), "", {
							type: SMap.WMMarker.TYPES.LITTLE,
							scale: 2, // skalovani ikon
						});
					}

					// cil
					let destination = null;

					// tahle cast je z vetsiny ukradena z /search/markers/index.ts a je to dost hruza - nicmene takhle tedy pracujeme s card a zmenou velikosti markeru
					if (subscription.routeParts?.length && subscription.routeParts.at(-1).poi.description) {
						const poi = {
							...subscription.routeParts.at(-1).poi.description,
							markerId: "little",
						};

						// @ts-ignore
						destination = Mapy.MarkerSearch.fromPOI(poi, undefined, {});

						const cont = destination.getContainer()[SMap.LAYER_MARKER];
						const callback = state => {
							if (state === "enter") {
								destination.isBlurring = false;
								if (poi.markerId === "little" && !destination.hasChagedType) {
									if (!(poi.review && reviewHasColoredBadge(poi.review)) && !poi.unreviewed) {
										description([poi], {
											reviews: true,
										}).then(function afterDescription(data) {
											destination.setType("small");
											destination.hasChagedType = true;
											destination.setActive(!isDetailActive(store.getState()));
											destination.setInteractive(true);
											if (addBadge(data.poi[0], cont)) {
												poi.review = data.poi[0].review;
											} else {
												poi.unreviewed = true;
											}
										}, () => {});
									} else {
										destination.setType("small");
										destination.hasChagedType = true;
										destination.setActive(!isDetailActive(store.getState()));
										destination.setInteractive(true);
										addBadge(poi, cont);
									}
								}
								setTimeout(() => {
									const card = destination.getCard();

									if (card) {
										const bottom = parseFloat(card.style.bottom);
										const top = parseFloat(cont.style.top);
										const anchor = destination.getCardAnchor(poi.markerId === "little" ? "small" : poi.markerId);

										if (bottom + top - anchor < -5) {
											card.style.zIndex = "101";
										}
									}
								}, 350);
							}

							// FIXME deje se i pri prejizdeni z markeru na kartu, coz je zbytecny
							if (state === "leave") {
								destination.isBlurring = true;
								setTimeout(() => {
									if (destination.isBlurring) {
										if (destination.hasChagedType) {
											destination.setType("little");
											delete destination.hasChagedType;
											destination.setActive(!isDetailActive(store.getState()));
										}
										const card = destination.getCard();

										if (card) {
											card.style.zIndex = null;
										}
										delete destination.isBlurring;
									}
								}, 200);
							}
						};

						cont.addEventListener("mouseenter", () => {
							cont.style.zIndex = "100";
							const card = destination.getCard();

							callback("enter");

							if (card.dataset.listen !== "1") {
								card.addEventListener("mouseenter", () => {
									callback("enter");
								});
								card.addEventListener("mouseleave", () => {
									callback("leave");
								});

								card.dataset.listen = "1";
							}
						});

						cont.addEventListener("mouseleave", () => {
							cont.style.zIndex = null;
							callback("leave");
						});
					}

					// zapamatujeme si vsechno pro budouci uziti
					newSubscriptions[subscription.subscriptionId] = { accuracy, avatar, geometryGone, geometryToGo, start, destination };
				}
			});

			// co zustalo v this.#subscriptions, to uz ve skutecnosti nechceme
			for (const subscriptionId in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
					this.#destroySubscription(this.#subscriptions[subscriptionId]);
				}
			}

			// nove aktualni si zapamatujeme
			this.#subscriptions = newSubscriptions;
		} else {
			// zrusime vsechno, co jsme mozna dosud ukazovali
			for (const subscriptionId in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
					this.#destroySubscription(this.#subscriptions[subscriptionId]);
				}
			}

			this.#subscriptions = {};
		}

		if (activeId in this.#subscriptions) {
			const { avatar, accuracy } = this.#subscriptions[activeId];

			// novou aktivni dostaneme do popredi
			this.#accuracyLayer.removeMarker(accuracy);
			this.#avatarLayer.removeMarker(avatar);
			this.#accuracyLayer.addMarker(accuracy);
			this.#avatarLayer.addMarker(avatar);
		}

		// pokud je nove aktivni, ukazeme jeji geometrii
		if (activeId !== oldActiveId) {
			if (activeId in this.#subscriptions) {
				const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[activeId];

				try {
					this.#geometryLayer.addGeometry(geometryGone);
					this.#geometryLayer.addGeometry(geometryToGo);
					start && this.#markerLayer.addMarker(start);
					destination && this.#markerLayer.addMarker(destination);

					// udelame bestfit
					const sceneCoords = [...geometryGone.getCoords(), ...geometryToGo.getCoords()].map(coord => {
						const [lon, lat] = coord.toWGS84();

						return Scene.coordsFromWGS84(lon, lat);
					});
					const centerZoom = sceneCoords.length ? Scene.computeCenterZoom(sceneCoords, true) : [avatar.getCoords(), NO_GEOMETRY_ZOOM];
					const [longitude, latitude] = centerZoom[0].toWGS84();
					const futureZoom = centerZoom[1] === Infinity || centerZoom[1] === -Infinity ? NO_GEOMETRY_ZOOM : centerZoom[1];
					const { zoom } = store.getState().map;

					store.dispatch(setCoordinates({
						longitude,
						latitude,
						zoom: futureZoom,
						animate: !CONFIG.inFrame && futureZoom >= zoom - 1 && futureZoom <= zoom + 1,
					}));
					Mapy.getComponent("sharePosition").activate();
					Mapy.getComponent("history").save();
				} catch (ex) {
					//
				}
			}
		}
	}

	#clearSharePosition() {
		for (const subscriptionId in this.#subscriptions) {
			if (Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
				this.#destroySubscription(this.#subscriptions[subscriptionId]);
			}
		}

		this.#subscriptions = {};
	}

	#destroySubscription(subscription: IMapSubscription) {
		const { accuracy, avatar, geometryGone, geometryToGo, start, destination } = subscription;

		this.#accuracyLayer.removeMarker(accuracy);
		this.#avatarLayer.removeMarker(avatar);
		accuracy.$destructor();
		avatar.$destructor();

		try {
			this.#geometryLayer.removeGeometry(geometryGone);
			geometryGone.$destructor();
			this.#geometryLayer.removeGeometry(geometryToGo);
			geometryToGo.$destructor();
		} catch (ex) {
			//
		}

		if (start) {
			this.#markerLayer.removeMarker(start);
			start.$destructor();
		}

		if (destination) {
			this.#markerLayer.removeMarker(destination);
			destination.$destructor();
		}
	}

	sharepositionActivated() {
		const { activeSubscription } = store.getState().sharePosition;

		for (const subscriptionId in this.#subscriptions) {
			if (subscriptionId !== activeSubscription && Object.prototype.hasOwnProperty.call(this.#subscriptions, subscriptionId)) {
				const { avatar, accuracy } = this.#subscriptions[subscriptionId];

				this.#accuracyLayer.addMarker(accuracy);
				this.#avatarLayer.addMarker(avatar);
			}
		}

		// tohle se dela posledni, aby skoncilo to prave vybrane nad ostatnimi v mape
		if (activeSubscription && activeSubscription in this.#subscriptions) {
			const { avatar, accuracy } = this.#subscriptions[activeSubscription];

			this.#accuracyLayer.addMarker(accuracy);
			this.#avatarLayer.addMarker(avatar);
		}
	}

	sharepositionDeactivated() {
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);

		subscriptions.forEach(subscription => {
			if (!subscription.attributes.showOnMap && subscription.subscriptionId in this.#subscriptions) {
				const { avatar, accuracy } = this.#subscriptions[subscription.subscriptionId];

				this.#accuracyLayer.removeMarker(accuracy);
				this.#avatarLayer.removeMarker(avatar);
			}
		});
	}
}
